import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const TR = (s: string) =>
  new Date(s).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });

const TR_AY = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
function ayLabel(ay: string) { return TR_AY[parseInt(ay.split('-')[1]) - 1]; }

const DURUMLAR = [
  { key: 'yeni',         label: 'Yeni',          renk: '#534AB7', bg: '#EEEDFE' },
  { key: 'iletisimde',   label: 'İletişimde',     renk: '#0C447C', bg: '#E6F1FB' },
  { key: 'ilgileniyor',  label: 'İlgileniyor',    renk: '#854F0B', bg: '#FAEEDA' },
  { key: 'deneme',       label: 'Deneme',         renk: '#3B6D11', bg: '#EAF3DE' },
  { key: 'kayit_oldu',   label: 'Kayıt Oldu ✓',  renk: '#085041', bg: '#C8F0E4' },
  { key: 'vazgecti',     label: 'Vazgeçti',       renk: '#888',    bg: '#f0f0f0' },
] as const;

const KAYNAKLAR = [
  { key: 'instagram',       label: 'Instagram' },
  { key: 'whatsapp',        label: 'WhatsApp' },
  { key: 'email',           label: 'E-posta' },
  { key: 'website',         label: 'Web sitesi' },
  { key: 'referans',        label: 'Referans' },
  { key: 'telefon',         label: 'Telefon' },
  { key: 'mobile_basvuru',  label: 'Mobil Uygulama' },
  { key: 'merge_request',   label: 'Birleştirme Talebi' },
  { key: 'diger',           label: 'Diğer' },
];

const KAYNAK_ICON: Record<string, string> = {
  instagram: '📸', whatsapp: '💬', email: '✉', website: '🌐',
  referans: '👥', telefon: '📞', mobile_basvuru: '📱', merge_request: '🔗', diger: '•',
};

const FUNNEL_RENKLER = ['#534AB7', '#0C447C', '#854F0B', '#3B6D11', '#085041'];

const durumBul = (key: string) => DURUMLAR.find(d => d.key === key) ?? DURUMLAR[0];

// Sekreterlerin kendi oluşturduğu serbest gruplar için tutarlı (ada göre hashlenmiş) renk —
// DB'de renk saklamıyoruz, aynı isim her zaman aynı renge düşsün yeter.
const ETIKET_RENKLERI = ['#534AB7', '#0C447C', '#854F0B', '#3B6D11', '#B45309', '#A32D2D', '#0F766E', '#7C3AED'];
function etiketRenk(ad: string) {
  let h = 0;
  for (let i = 0; i < ad.length; i++) h = (h * 31 + ad.charCodeAt(i)) >>> 0;
  return ETIKET_RENKLERI[h % ETIKET_RENKLERI.length];
}

interface Aday {
  id: string; ad: string; soyad: string; email: string; tel: string;
  kaynak: string; durum: string; ilgi_alani: string; notlar: string;
  ogrenci_id: string | null; olusturuldu: string; guncellendi: string;
  gorusme_sayisi: number; son_gorusme: string | null; son_gorusme_icerik: string | null;
  sube_id?: string; sube_adi?: string;
  referans_ogrenci_ad?: string;
  ilgilenilen_ders_id?: string | null; ilgilenilen_ders_adi?: string | null; ilgilenilen_ders_seviye?: string | null;
  etiketler?: { id: string; ad: string }[];
}
interface Ders { id: string; ad: string; seviye: string; sube_id?: string; aktif: boolean; }
interface Etiket { id: string; ad: string; sube_id?: string | null; kullanim_sayisi: number; }
interface Istatistik { toplam: number; yeni: number; iletisimde: number; ilgileniyor: number; deneme: number; kayit_oldu: number; vazgecti: number; }
interface Performans {
  funnel: { durum: string; label: string; sayi: number }[];
  donusum_orani: number;
  pipeline_toplam: number;
  vazgecme_orani: number;
  ortalama_kapalis_gun: number;
  kaynak_breakdown: { kaynak: string; toplam: number; kayit: number; oran: number }[];
  aylik_trend: { ay: string; yeni_lead: number; kayit: number }[];
  sinif_dagilimi: { ders_id: string; ders_adi: string; seviye: string; sube_adi: string | null; toplam: number; kayit: number; oran: number }[];
}

const BOSH_FORM = { ad: '', soyad: '', email: '', tel: '', kaynak: 'instagram', durum: 'yeni', ilgi_alani: '', notlar: '', cinsiyet: '', ilgilenilen_ders_id: '', etiket_idleri: [] as string[] };

export default function CRM() {
  usePageTitle('CRM');
  const navigate = useNavigate();
  const { kullanici } = useAuth();
  const isSuperAdmin = kullanici?.rol === 'super_admin';
  const { isDark } = useTheme();
  const card        = isDark ? '#1a1a1a' : 'white';
  const cardAlt     = isDark ? '#1e1e1e' : '#f8f8f8';
  const cardHover   = isDark ? '#252525' : '#fafafa';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const borderSub   = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';

  const [adaylar, setAdaylar]           = useState<Aday[]>([]);
  const [istatistik, setIstatistik]     = useState<Istatistik | null>(null);
  const [performans, setPerformans]     = useState<Performans | null>(null);
  const [panelAcik, setPanelAcik]       = useState(true);
  const [yukleniyor, setYukleniyor]     = useState(true);
  const [aktifDurum, setAktifDurum]     = useState<string>('yeni');
  const [aktifKaynak, setAktifKaynak]   = useState<string>('');
  const [arama, setArama]               = useState('');
  const [aktifSube, setAktifSube]       = useState<string>('');
  const [aktifDers, setAktifDers]       = useState<string>('');
  const [aktifEtiket, setAktifEtiket]   = useState<string>('');
  const [subeler, setSubeler]           = useState<{ id: string; ad: string }[]>([]);
  const [dersler, setDersler]           = useState<Ders[]>([]);
  const [etiketler, setEtiketler]       = useState<Etiket[]>([]);
  const [modal, setModal]               = useState(false);
  const [form, setForm]                 = useState(BOSH_FORM);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata]                 = useState('');
  const [etiketYonetModal, setEtiketYonetModal] = useState(false);
  const [yardimModal, setYardimModal] = useState(false);
  const [yeniEtiketAd, setYeniEtiketAd] = useState('');
  const [etiketIsleniyor, setEtiketIsleniyor] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      api.get('/api/subeler').then(r => setSubeler(r.data)).catch(() => {});
    }
    api.get('/api/dersler').then(r => setDersler(r.data)).catch(() => {});
  }, [isSuperAdmin]);

  const etiketlerYukle = useCallback(async () => {
    try {
      const r = await api.get('/api/crm/etiketler', { params: isSuperAdmin && aktifSube ? { sube_id: aktifSube } : {} });
      setEtiketler(r.data);
    } catch (e) { console.error(e); }
  }, [isSuperAdmin, aktifSube]);
  useEffect(() => { etiketlerYukle(); }, [etiketlerYukle]);

  // Adaya bağlanabilecek dersler: super_admin bir şube seçtiyse ona göre daralt,
  // seçmediyse tüm şubelerin dersleri (isim yanında şube adı gösterilir); sube_admin
  // için backend zaten sadece kendi şubesini döndürüyor.
  const subeAdMap = React.useMemo(() => Object.fromEntries(subeler.map(s => [s.id, s.ad])), [subeler]);
  const dersOptions = React.useMemo(
    () => dersler.filter(d => !isSuperAdmin || !aktifSube || d.sube_id === aktifSube),
    [dersler, isSuperAdmin, aktifSube]
  );

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const params: any = {};
      if (aktifDurum)              params.durum   = aktifDurum;
      if (aktifKaynak)             params.kaynak  = aktifKaynak;
      if (aktifDers)               params.ders_id   = aktifDers;
      if (aktifEtiket)             params.etiket_id = aktifEtiket;
      if (arama)                   params.arama   = arama;
      if (isSuperAdmin && aktifSube) params.sube_id = aktifSube;
      const [adayRes, statRes] = await Promise.all([
        api.get('/api/crm/adaylar', { params }),
        api.get('/api/crm/istatistik', { params: isSuperAdmin && aktifSube ? { sube_id: aktifSube } : {} }),
      ]);
      setAdaylar(adayRes.data);
      setIstatistik(statRes.data);
    } catch (e) { console.error(e); }
    finally { setYukleniyor(false); }
  }, [aktifDurum, aktifKaynak, aktifDers, aktifEtiket, arama, aktifSube, isSuperAdmin]);

  useEffect(() => { yukle(); }, [yukle]);

  const performansYukle = useCallback(async () => {
    try {
      const params: any = {};
      if (isSuperAdmin && aktifSube) params.sube_id = aktifSube;
      const r = await api.get('/api/crm/performans', { params });
      setPerformans(r.data);
    } catch (e) { console.error(e); }
  }, [aktifSube, isSuperAdmin]);

  useEffect(() => { performansYukle(); }, [performansYukle]);


  const kaydet = async () => {
    if (!form.ad.trim()) { setHata('Ad zorunlu'); return; }
    setKaydediliyor(true); setHata('');
    try {
      const res = await api.post('/api/crm/adaylar', form);
      if (form.etiket_idleri.length) {
        await Promise.all(form.etiket_idleri.map(eid =>
          api.post(`/api/crm/adaylar/${res.data.id}/etiketler`, { etiket_id: eid }).catch(() => {})
        ));
      }
      setModal(false);
      setForm(BOSH_FORM);
      yukle();
      performansYukle();
      etiketlerYukle();
    } catch (e: any) { setHata(e.response?.data?.hata ?? 'Hata oluştu'); }
    finally { setKaydediliyor(false); }
  };

  const etiketToggle = (eid: string) => {
    setForm(p => ({
      ...p,
      etiket_idleri: p.etiket_idleri.includes(eid) ? p.etiket_idleri.filter(x => x !== eid) : [...p.etiket_idleri, eid],
    }));
  };

  const yeniEtiketEkle = async () => {
    if (!yeniEtiketAd.trim()) return;
    setEtiketIsleniyor(true);
    try {
      const r = await api.post('/api/crm/etiketler', { ad: yeniEtiketAd.trim() });
      setEtiketler(p => [...p, r.data].sort((a, b) => a.ad.localeCompare(b.ad, 'tr')));
      setForm(p => ({ ...p, etiket_idleri: [...p.etiket_idleri, r.data.id] }));
      setYeniEtiketAd('');
    } catch (e: any) { alert(e.response?.data?.hata || 'Grup eklenemedi'); }
    finally { setEtiketIsleniyor(false); }
  };

  const etiketSil = async (e: Etiket) => {
    const msg = e.kullanim_sayisi > 0
      ? `"${e.ad}" grubu silinsin mi?\n\n${e.kullanim_sayisi} adaydan da kaldırılacak.`
      : `"${e.ad}" grubu silinsin mi?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/api/crm/etiketler/${e.id}`);
      setEtiketler(p => p.filter(x => x.id !== e.id));
      setForm(p => ({ ...p, etiket_idleri: p.etiket_idleri.filter(x => x !== e.id) }));
      yukle();
    } catch (err: any) { alert(err.response?.data?.hata || 'Silinemedi'); }
  };

  const gorunenAdaylar = adaylar;

  const yMax = performans
    ? Math.max(...performans.aylik_trend.map(a => Math.max(a.yeni_lead, a.kayit)), 1)
    : 1;

  return (
    <div style={{ padding: '24px 24px 48px' }}>

      {/* Başlık */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>CRM</h1>
          <div style={{ fontSize: 13, color: textMuted, marginTop: 2 }}>Potansiyel müşteri takibi</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setYardimModal(true)}
            style={{ background: card, color: textSec, border: '1px solid #e0e0e0', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
            ❓ Nasıl Kullanılır
          </button>
          <button onClick={() => setPanelAcik(p => !p)}
            style={{ background: card, color: textSec, border: '1px solid #e0e0e0', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
            {panelAcik ? '▲ Performansı gizle' : '▼ Performansı göster'}
          </button>
          <button onClick={() => { setYeniEtiketAd(''); setEtiketYonetModal(true); }}
            style={{ background: card, color: textSec, border: '1px solid #e0e0e0', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
            🏷️ Gruplar
          </button>
          <button onClick={() => { setForm(BOSH_FORM); setHata(''); setModal(true); }}
            style={{ background: '#534AB7', color: 'white', border: 'none', padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            + Yeni aday
          </button>
        </div>
      </div>

      {/* ─── Performans Paneli ─────────────────────────────────────────── */}
      {panelAcik && performans && (
        <div style={{ marginBottom: 20, background: card, border: `1px solid ${border}`, borderRadius: 12, padding: 20 }}>

          {/* 4 metrik kart */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
            <MetrikKart
              baslik="Dönüşüm Oranı"
              deger={`%${performans.donusum_orani}`}
              alt="kayıt / (kayıt + vazgeçti)"
              renk="#085041"
              pct={performans.donusum_orani}
              pctRenk="#085041"
            />
            <MetrikKart
              baslik="Aktif Pipeline"
              deger={String(performans.pipeline_toplam)}
              alt="süreçteki toplam aday"
              renk="#0C447C"
              pct={istatistik ? Math.round((performans.pipeline_toplam / Number(istatistik.toplam)) * 100) : 0}
              pctRenk="#0C447C"
            />
            <MetrikKart
              baslik="Ort. Kapanış Süresi"
              deger={performans.ortalama_kapalis_gun ? `${performans.ortalama_kapalis_gun} gün` : '—'}
              alt="lead → kayıt ortalama"
              renk="#854F0B"
            />
            <MetrikKart
              baslik="Vazgeçme Oranı"
              deger={`%${performans.vazgecme_orani}`}
              alt="tüm leadlerde vazgeçme"
              renk="#888"
              pct={performans.vazgecme_orani}
              pctRenk="#ccc"
            />
          </div>

          {/* Huni + Kaynak ─ yan yana */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>

            {/* Dönüşüm Hunisi */}
            <div>
              <SekmeBaslik>Dönüşüm Hunisi</SekmeBaslik>
              {performans.funnel.map((f, i) => {
                const maks = performans.funnel[0].sayi || 1;
                const pct  = Math.round((f.sayi / maks) * 100);
                return (
                  <div key={f.durum} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                    <div style={{ width: 82, fontSize: 11, color: textSec, textAlign: 'right', flexShrink: 0, paddingRight: 4 }}>
                      {f.label}
                    </div>
                    <div style={{ flex: 1, height: 22, background: isDark ? '#333' : '#f4f4f4', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        width: `${pct}%`, height: '100%',
                        background: FUNNEL_RENKLER[i],
                        borderRadius: 4,
                        display: 'flex', alignItems: 'center', paddingLeft: 8,
                        transition: 'width 0.6s ease',
                        minWidth: f.sayi > 0 ? 28 : 0,
                      }}>
                        {f.sayi > 0 && (
                          <span style={{ fontSize: 11, color: 'white', fontWeight: 700 }}>{f.sayi}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ width: 34, fontSize: 11, color: isDark ? '#555' : '#bbb', textAlign: 'right', flexShrink: 0 }}>
                      {i === 0 ? '100%' : `${pct}%`}
                    </div>
                  </div>
                );
              })}
              <div style={{ marginTop: 10, fontSize: 11, color: isDark ? '#555' : '#bbb', textAlign: 'right' }}>
                * yüzde ilk aşamaya göre
              </div>
            </div>

            {/* Kaynak Performansı */}
            <div>
              <SekmeBaslik>Kaynak Performansı</SekmeBaslik>
              {performans.kaynak_breakdown.length === 0 ? (
                <div style={{ fontSize: 12, color: isDark ? '#555' : '#ccc', textAlign: 'center', paddingTop: 20 }}>Veri yok</div>
              ) : performans.kaynak_breakdown.map(k => {
                const kLabel = KAYNAKLAR.find(x => x.key === k.kaynak)?.label ?? k.kaynak;
                const icon   = KAYNAK_ICON[k.kaynak] ?? '•';
                return (
                  <div key={k.kaynak} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
                    <div style={{ width: 82, fontSize: 11, color: isDark ? '#999' : '#666', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                      <span style={{ fontSize: 14 }}>{icon}</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{kLabel}</span>
                    </div>
                    <div style={{ flex: 1, height: 16, background: isDark ? '#222222' : '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        width: `${k.oran}%`, height: '100%',
                        background: k.oran >= 50 ? '#085041' : k.oran >= 25 ? '#3B6D11' : '#B45309',
                        borderRadius: 3, transition: 'width 0.6s ease',
                      }} />
                    </div>
                    <div style={{ width: 72, fontSize: 10, color: textSec, textAlign: 'right', flexShrink: 0 }}>
                      {k.kayit}/{k.toplam} · %{k.oran}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sınıf Dağılımı */}
          {performans.sinif_dagilimi.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <SekmeBaslik>Sınıfa Göre İlgi Dağılımı</SekmeBaslik>
              <div style={{ display: 'grid', gap: 7 }}>
                {performans.sinif_dagilimi.map(s => (
                  <div key={s.ders_id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 200, fontSize: 11, color: isDark ? '#999' : '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}
                      title={`${s.ders_adi} (${s.seviye}${s.sube_adi ? `, ${s.sube_adi}` : ''})`}>
                      {s.ders_adi} <span style={{ color: textMuted }}>· {s.seviye}</span>
                    </div>
                    <div style={{ flex: 1, height: 16, background: isDark ? '#222222' : '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        width: `${s.oran}%`, height: '100%',
                        background: s.oran >= 50 ? '#085041' : s.oran >= 25 ? '#3B6D11' : '#B45309',
                        borderRadius: 3, transition: 'width 0.6s ease',
                      }} />
                    </div>
                    <div style={{ width: 90, fontSize: 10, color: textSec, textAlign: 'right', flexShrink: 0 }}>
                      {s.kayit}/{s.toplam} · %{s.oran}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Aylık Trend */}
          <div>
            <SekmeBaslik>Aylık Lead & Kayıt (son 6 ay)</SekmeBaslik>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 90, paddingBottom: 24, position: 'relative' }}>
              {performans.aylik_trend.map(a => (
                <div key={a.ay} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                  <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 66 }}>
                    <div
                      title={`${a.yeni_lead} yeni lead`}
                      style={{
                        width: 14, borderRadius: '3px 3px 0 0',
                        height: Math.max((a.yeni_lead / yMax) * 66, a.yeni_lead > 0 ? 3 : 0),
                        background: '#C7C5F4', transition: 'height 0.5s ease',
                      }}
                    />
                    <div
                      title={`${a.kayit} kayıt`}
                      style={{
                        width: 14, borderRadius: '3px 3px 0 0',
                        height: Math.max((a.kayit / yMax) * 66, a.kayit > 0 ? 3 : 0),
                        background: '#085041', transition: 'height 0.5s ease',
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 10, color: textMuted, marginTop: 4, textAlign: 'center' }}>{ayLabel(a.ay)}</div>
                  {a.yeni_lead > 0 && (
                    <div style={{ fontSize: 9, color: isDark ? '#555' : '#bbb', textAlign: 'center' }}>{a.yeni_lead}</div>
                  )}
                </div>
              ))}
            </div>
            {/* Lejant */}
            <div style={{ display: 'flex', gap: 16, marginTop: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: textSec }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: '#C7C5F4' }} />
                Yeni lead
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: textSec }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: '#085041' }} />
                Kayıt oldu
              </div>
            </div>
          </div>

        </div>
      )}

      {/* İstatistik sekmeler */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {DURUMLAR.map(d => (
          <DurumSekme key={d.key}
            label={d.label} count={istatistik ? Number((istatistik as any)[d.key]) : 0}
            aktif={aktifDurum === d.key} renk={d.renk} bg={d.bg}
            onClick={() => setAktifDurum(p => p === d.key ? '' : d.key)}
          />
        ))}
        <DurumSekme
          label="Tümü" count={istatistik ? Number(istatistik.toplam) : 0}
          aktif={aktifDurum === ''} renk="#534AB7" bg="#EEEDFE"
          onClick={() => setAktifDurum('')}
        />
      </div>

      {/* Filtre + arama */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={arama} onChange={e => setArama(e.target.value)}
          placeholder="Ad, e-posta veya telefon ara..."
          style={{ flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}
        />
        <select value={aktifKaynak} onChange={e => setAktifKaynak(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
          <option value="">Tüm kaynaklar</option>
          {KAYNAKLAR.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
        <select value={aktifDers} onChange={e => setAktifDers(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, color: aktifDers ? (isDark ? '#e0e0e0' : '#333') : textMuted, maxWidth: 220 }}>
          <option value="">Tüm sınıflar</option>
          {dersOptions.map(d => (
            <option key={d.id} value={d.id}>
              {d.ad} · {d.seviye}{isSuperAdmin && !aktifSube && d.sube_id ? ` — ${subeAdMap[d.sube_id] ?? ''}` : ''}
            </option>
          ))}
        </select>
        <select value={aktifEtiket} onChange={e => setAktifEtiket(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, color: aktifEtiket ? (isDark ? '#e0e0e0' : '#333') : textMuted, maxWidth: 180 }}>
          <option value="">Tüm gruplar</option>
          {etiketler.map(e => <option key={e.id} value={e.id}>{e.ad} ({e.kullanim_sayisi})</option>)}
        </select>
        {isSuperAdmin && (
          <select value={aktifSube} onChange={e => setAktifSube(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, color: aktifSube ? isDark ? '#e0e0e0' : '#333' : textMuted, maxWidth: 200 }}>
            <option value="">Tüm şubeler</option>
            {subeler.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
          </select>
        )}
      </div>

      {/* Liste */}
      {yukleniyor ? (
        <div style={{ textAlign: 'center', padding: 60, color: textMuted, fontSize: 14 }}>Yükleniyor...</div>
      ) : gorunenAdaylar.length === 0 ? (
        <div style={{ background: card, border: '1px dashed #ddd', borderRadius: 10, padding: '50px 24px', textAlign: 'center', color: textMuted, fontSize: 13 }}>
          {arama || aktifDurum || aktifKaynak || aktifDers || aktifEtiket ? 'Filtreyle eşleşen aday bulunamadı.' : 'Henüz aday eklenmemiş. "+ Yeni aday" ile başlayın.'}
        </div>
      ) : (
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
          {gorunenAdaylar.map((a, i) => {
            const dur = durumBul(a.durum);
            const src = KAYNAKLAR.find(k => k.key === a.kaynak);
            return (
              <div key={a.id}
                onClick={() => navigate(`/crm/${a.id}`)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
                  borderBottom: i < gorunenAdaylar.length - 1 ? `1px solid ${borderSub}` : 'none',
                  cursor: 'pointer', transition: 'background .12s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = cardHover)}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: dur.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: dur.renk, flexShrink: 0 }}>
                  {a.ad?.charAt(0)}{a.soyad?.charAt(0) ?? ''}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{a.ad} {a.soyad}</div>
                  <div style={{ fontSize: 12, color: textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.son_gorusme_icerik
                      ? `${a.son_gorusme_icerik.slice(0, 60)}${a.son_gorusme_icerik.length > 60 ? '…' : ''}`
                      : a.email || a.tel || 'Henüz görüşme yok'}
                  </div>
                </div>
                <div style={{ fontSize: 18, flexShrink: 0 }} title={a.kaynak === 'referans' && a.referans_ogrenci_ad ? `Referans: ${a.referans_ogrenci_ad}` : src?.label}>
                  {KAYNAK_ICON[a.kaynak] ?? '•'}
                  {a.kaynak === 'referans' && a.referans_ogrenci_ad && (
                    <div style={{ fontSize: 9, color: textSec, marginTop: 1, maxWidth: 60, textAlign: 'center', lineHeight: '1.2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {a.referans_ogrenci_ad.split(' ')[0]}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: textMuted, flexShrink: 0, textAlign: 'center', minWidth: 32 }}>
                  <div style={{ fontWeight: 600, color: isDark ? '#bbb' : '#555', fontSize: 13 }}>{a.gorusme_sayisi}</div>
                  görüşme
                </div>
                <div style={{ fontSize: 11, color: isDark ? '#555' : '#bbb', flexShrink: 0, textAlign: 'right', minWidth: 70 }}>
                  {a.son_gorusme ? TR(a.son_gorusme) : TR(a.olusturuldu)}
                </div>
                {a.etiketler?.map(e => (
                  <span key={e.id} style={{ background: etiketRenk(e.ad) + '22', color: etiketRenk(e.ad), borderRadius: 20, padding: '3px 9px', fontSize: 10, fontWeight: 600, flexShrink: 0, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    🏷️ {e.ad}
                  </span>
                ))}
                {a.ilgilenilen_ders_adi && (
                  <span title={a.ilgilenilen_ders_adi} style={{ background: '#FAEEDA', color: '#854F0B', borderRadius: 20, padding: '3px 9px', fontSize: 10, fontWeight: 600, flexShrink: 0, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    🏫 {a.ilgilenilen_ders_adi}
                  </span>
                )}
                {isSuperAdmin && a.sube_adi && (
                  <span style={{ background: '#F0F0FF', color: '#6366F1', borderRadius: 20, padding: '3px 9px', fontSize: 10, fontWeight: 600, flexShrink: 0, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.sube_adi}
                  </span>
                )}
                <span style={{ background: dur.bg, color: dur.renk, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                  {dur.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Birleştirme Talepleri ─────────────────────────────────────── */}
      {/* Yeni aday modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setModal(false); }}>
          <div style={{ background: card, borderRadius: 12, padding: 24, width: '100%', maxWidth: 460 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 18 }}>Yeni aday ekle</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <Label>Ad *</Label>
                <input value={form.ad} onChange={e => setForm(p => ({ ...p, ad: e.target.value }))} style={inputSt} placeholder="Ad" />
              </div>
              <div>
                <Label>Soyad</Label>
                <input value={form.soyad} onChange={e => setForm(p => ({ ...p, soyad: e.target.value }))} style={inputSt} placeholder="Soyad" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <Label>E-posta</Label>
                <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} style={inputSt} placeholder="ornek@gmail.com" />
              </div>
              <div>
                <Label>Telefon</Label>
                <input value={form.tel} onChange={e => setForm(p => ({ ...p, tel: e.target.value }))} style={inputSt} placeholder="05xx xxx xx xx" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <Label>Kaynak</Label>
                <select value={form.kaynak} onChange={e => setForm(p => ({ ...p, kaynak: e.target.value }))} style={selectSt}>
                  {KAYNAKLAR.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Durum</Label>
                <select value={form.durum} onChange={e => setForm(p => ({ ...p, durum: e.target.value }))} style={selectSt}>
                  {DURUMLAR.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Cinsiyet</Label>
                <select value={form.cinsiyet} onChange={e => setForm(p => ({ ...p, cinsiyet: e.target.value }))} style={selectSt}>
                  <option value=''>—</option>
                  <option value='kadin'>Kadın</option>
                  <option value='erkek'>Erkek</option>
                  <option value='diger'>Diğer</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <Label>İlgilendiği sınıf (opsiyonel)</Label>
              <select value={form.ilgilenilen_ders_id} onChange={e => setForm(p => ({ ...p, ilgilenilen_ders_id: e.target.value }))} style={selectSt}>
                <option value="">— Seçilmedi —</option>
                {dersOptions.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.ad} · {d.seviye}{isSuperAdmin && !aktifSube && d.sube_id ? ` — ${subeAdMap[d.sube_id] ?? ''}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 10 }}>
              <Label>Gruplar (opsiyonel — örn. "Haftasonu Bekleyenler")</Label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                {etiketler.map(e => {
                  const secili = form.etiket_idleri.includes(e.id);
                  const renk = etiketRenk(e.ad);
                  return (
                    <button key={e.id} type="button" onClick={() => etiketToggle(e.id)}
                      style={{ border: `1px solid ${secili ? renk : '#ddd'}`, background: secili ? renk + '22' : card, color: secili ? renk : isDark ? '#bbb' : '#555', borderRadius: 20, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                      {secili ? '✓ ' : ''}{e.ad}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input value={yeniEtiketAd} onChange={e => setYeniEtiketAd(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); yeniEtiketEkle(); } }}
                  placeholder="+ Yeni grup adı yaz, Enter'a bas..." style={{ ...inputSt, flex: 1 }} />
                <button type="button" onClick={yeniEtiketEkle} disabled={etiketIsleniyor || !yeniEtiketAd.trim()}
                  style={{ padding: '0 14px', borderRadius: 6, border: '1px solid #ddd', background: card, cursor: 'pointer', fontSize: 12, color: '#534AB7', fontWeight: 600, opacity: !yeniEtiketAd.trim() ? 0.5 : 1 }}>
                  Ekle
                </button>
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <Label>İlgi alanı (ek not)</Label>
              <input value={form.ilgi_alani} onChange={e => setForm(p => ({ ...p, ilgi_alani: e.target.value }))} style={inputSt} placeholder="Örn: Salsa, hafta sonu tercih ediyor..." />
            </div>

            <div style={{ marginBottom: 16 }}>
              <Label>Not</Label>
              <textarea value={form.notlar} onChange={e => setForm(p => ({ ...p, notlar: e.target.value }))} rows={3}
                style={{ ...inputSt, resize: 'vertical' as const }} placeholder="İlk izlenimler, notlar..." />
            </div>

            {hata && <div style={{ background: isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{hata}</div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(false)} style={btnSt}>İptal</button>
              <button onClick={kaydet} disabled={kaydediliyor}
                style={{ ...btnSt, background: '#534AB7', color: 'white', border: 'none' }}>
                {kaydediliyor ? 'Kaydediliyor...' : 'Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gruplar yönet modalı */}
      {etiketYonetModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setEtiketYonetModal(false); }}>
          <div style={{ background: card, borderRadius: 12, padding: 24, width: '100%', maxWidth: 400 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>🏷️ Gruplar</div>
            <div style={{ fontSize: 12, color: textMuted, marginBottom: 14 }}>Henüz derse atanmamış adayları serbestçe gruplamak için — şubedeki herkes görür ve kullanır.</div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
              <input value={yeniEtiketAd} onChange={e => setYeniEtiketAd(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); yeniEtiketEkle(); } }}
                placeholder="Yeni grup adı..." style={{ ...inputSt, flex: 1 }} />
              <button onClick={yeniEtiketEkle} disabled={etiketIsleniyor || !yeniEtiketAd.trim()}
                style={{ padding: '0 14px', borderRadius: 6, border: 'none', background: '#534AB7', color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600, opacity: !yeniEtiketAd.trim() ? 0.5 : 1 }}>
                Ekle
              </button>
            </div>

            <div style={{ display: 'grid', gap: 6, maxHeight: 280, overflowY: 'auto' }}>
              {etiketler.length === 0 ? (
                <div style={{ textAlign: 'center', color: textMuted, fontSize: 12, padding: '20px 0' }}>Henüz grup yok.</div>
              ) : etiketler.map(e => (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', background: cardAlt, borderRadius: 6 }}>
                  <span style={{ fontSize: 13, color: etiketRenk(e.ad), fontWeight: 600 }}>
                    {e.ad} <span style={{ color: textMuted, fontWeight: 400, fontSize: 11 }}>({e.kullanim_sayisi} aday)</span>
                  </span>
                  <button onClick={() => etiketSil(e)} title="Sil"
                    style={{ border: 'none', background: 'none', color: '#e24b4a', cursor: 'pointer', fontSize: 13, padding: 4 }}>
                    🗑
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setEtiketYonetModal(false)} style={btnSt}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* Yardım modalı */}
      {yardimModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setYardimModal(false); }}>
          <div style={{ background: card, borderRadius: 12, padding: 24, width: '100%', maxWidth: 620, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 2 }}>❓ CRM Nasıl Kullanılır</div>
            <div style={{ fontSize: 12, color: textMuted, marginBottom: 20 }}>Potansiyel öğrenci takibinin tüm adımları</div>

            <YardimBolum baslik="1. Durum Akışı (Huni)">
              Her aday üstteki sekmelerden birinde: <b>Yeni</b> → <b>İletişimde</b> → <b>İlgileniyor</b> → <b>Deneme</b> → <b>Kayıt Oldu ✓</b> (ya da yolda <b>Vazgeçti</b>).
              Sekmelere tıklayınca listede sadece o durumdakiler görünür — tekrar tıklarsan filtre kalkar. Bir adayın durumunu değiştirmek için adayın satırına tıklayıp detay sayfasını aç, oradan durumu güncelle.
            </YardimBolum>

            <YardimBolum baslik="2. Yeni Aday Ekleme">
              Sağ üstteki <b>"+ Yeni aday"</b> butonuyla açılan formda:
              <ul style={ulSt}>
                <li><b>Kaynak</b> — adayın nereden geldiği (Instagram, WhatsApp, Referans, Telefon...)</li>
                <li><b>İlgilendiği sınıf</b> — eğer belirli bir ders/sınıfla ilgileniyorsa, gerçek ders listesinden seçilir (yazım hatası olmaz, "Sınıfa göre" filtresiyle ve Performans panelindeki "Sınıfa Göre İlgi Dağılımı"yla eşleşir)</li>
                <li><b>Gruplar</b> — henüz belirli bir sınıfa karar verilmemişse, kendi serbest etiketini kullan (bkz. madde 4)</li>
                <li><b>İlgi alanı</b> ve <b>Not</b> — serbest metin, ek bilgi için</li>
              </ul>
            </YardimBolum>

            <YardimBolum baslik="3. Aday Detayı — Görüşme, Hatırlatıcı, WhatsApp">
              Bir adayın satırına tıklayınca detay sayfası açılır. Orada:
              <ul style={ulSt}>
                <li><b>Görüşme ekle</b> — her telefon/WhatsApp/yüz yüze görüşmeyi kaydet, aday listesinde "son görüşme" olarak görünür</li>
                <li><b>🔔 Hatırlatıcı</b> — belirli bir tarihte "ara/takip et" hatırlatması kur, CRM'deki Alarmlar listesinde çıkar</li>
                <li><b>WhatsApp</b> sekmesinden doğrudan yazışabilirsin (varsa)</li>
                <li><b>Şubeye yönlendir</b> — aday yanlış şubeye düşmüşse başka şubeye taşı</li>
                <li><b>Mevcut öğrenciyle birleştir</b> — aynı kişi zaten öğrenciyse, yanlış kaydı düzeltmek için</li>
              </ul>
            </YardimBolum>

            <YardimBolum baslik="4. İlgilendiği Sınıf ile Gruplar Arasındaki Fark">
              İkisi de "bu aday ne istiyor" bilgisini taşır ama farklı amaçlar için:
              <ul style={ulSt}>
                <li><b>🏫 İlgilendiği sınıf</b> — gerçek, takvimde var olan bir ders kaydına bağlanır. Kesinleşmiş, planlı bir sınıfla ilgileniyorsa bunu kullan.</li>
                <li><b>🏷️ Gruplar</b> — sen ne isim verirsen o olur, önceden tanımlı değildir ("Haftasonu Bekleyenler", "Kasım ayı bekleyenler", "21:00 Bekleyenler", "Milonga Seminer Bekleyenler" gibi). Henüz kesin bir sınıfa karar verilmemiş ama takip edilmesi gereken adaylar için idealdir. Üstteki <b>"🏷️ Gruplar"</b> butonundan tüm grupları yönetebilir (oluştur/sil), bir adayın profilinden <b>"+ Grup Ekle"</b> ile o adayı gruba ekleyip çıkarabilirsin. Gruplar şubedeki herkesle paylaşılır, bir aday aynı anda birden fazla grupta olabilir.</li>
              </ul>
            </YardimBolum>

            <YardimBolum baslik="5. Filtreleme ve Arama">
              Arama kutusu ad/e-posta/telefonda arar. Yanındaki dropdown'lar: Kaynak, Sınıf, Grup{isSuperAdmin ? ', Şube (süper admin)' : ''} — hepsi birlikte kullanılabilir, birbirini daraltır.
            </YardimBolum>

            <YardimBolum baslik="6. Öğrenciye Dönüştürme">
              Aday durumu <b>"Kayıt Oldu"</b> yapılınca, e-posta adresi varsa sistem otomatik bir öğrenci kaydı oluşturup adaya bağlar (aynı e-posta ile zaten öğrenciyse ona bağlanır). E-posta yoksa uyarı çıkar — önce öğrenciyi elle eklemen, sonra "Mevcut öğrenciyle birleştir" ile bağlaman gerekir.
            </YardimBolum>

            <YardimBolum baslik="7. Performans Paneli">
              Üstteki <b>"▼ Performansı göster"</b> ile açılır: dönüşüm oranı, aktif pipeline (süreçteki toplam aday), ortalama kapanış süresi, vazgeçme oranı, dönüşüm hunisi, kaynak performansı, sınıfa göre ilgi dağılımı ve son 6 aylık lead/kayıt trendi. Süper adminler bir şube seçerek sadece o şubenin performansına bakabilir.
            </YardimBolum>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button onClick={() => setYardimModal(false)} style={{ ...btnSt, background: '#534AB7', color: 'white', border: 'none' }}>Anladım</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Alt bileşenler ───────────────────────────────────────────────────────────

function MetrikKart({ baslik, deger, alt, renk, pct, pctRenk }: {
  baslik: string; deger: string; alt: string; renk: string; pct?: number; pctRenk?: string;
}) {
  const { isDark } = useTheme();
  const cardHover   = isDark ? '#252525' : '#fafafa';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const textMuted   = isDark ? '#666'    : '#aaa';
  return (
    <div style={{ background: cardHover, border: `1px solid ${border}`, borderRadius: 10, padding: '16px 18px' }}>
      <div style={{ fontSize: 10, color: textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>{baslik}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color: renk, marginBottom: 8, lineHeight: 1 }}>{deger}</div>
      {pct !== undefined && pctRenk && (
        <div style={{ height: 4, background: isDark ? '#333' : '#ebebeb', borderRadius: 2, marginBottom: 8 }}>
          <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: pctRenk, borderRadius: 2, transition: 'width 0.6s ease' }} />
        </div>
      )}
      <div style={{ fontSize: 11, color: isDark ? '#555' : '#bbb' }}>{alt}</div>
    </div>
  );
}

function SekmeBaslik({ children }: { children: React.ReactNode }) {
  const { isDark } = useTheme();
  const textMuted   = isDark ? '#666'    : '#aaa';
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 12 }}>
      {children}
    </div>
  );
}

function DurumSekme({ label, count, aktif, renk, bg, onClick }: {
  label: string; count: number; aktif: boolean; renk: string; bg: string; onClick: () => void;
}) {
  const { isDark } = useTheme();
  const card    = isDark ? '#1a1a1a' : 'white';
  const border  = isDark ? '#2a2a2a' : '#eee';
  const textSec = isDark ? '#999'    : '#888';
  return (
    <button onClick={onClick} style={{
      padding: '6px 14px', borderRadius: 20, border: `1.5px solid ${aktif ? renk : border}`,
      background: aktif ? bg : card, color: aktif ? renk : textSec,
      cursor: 'pointer', fontSize: 12, fontWeight: aktif ? 700 : 400,
      display: 'flex', alignItems: 'center', gap: 6,
    }}>
      {label}
      <span style={{ background: aktif ? renk : isDark ? '#2a2a2a' : '#f0f0f0', color: aktif ? 'white' : textSec, borderRadius: 20, padding: '1px 6px', fontSize: 11, fontWeight: 700 }}>
        {count}
      </span>
    </button>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  const { isDark } = useTheme();
  const textSec = isDark ? '#999' : '#888';
  return <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 500 }}>{children}</div>;
}

function YardimBolum({ baslik, children }: { baslik: string; children: React.ReactNode }) {
  const { isDark } = useTheme();
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const border      = isDark ? '#2a2a2a' : '#eee';
  return (
    <div style={{ marginBottom: 18, paddingBottom: 18, borderBottom: `1px solid ${border}` }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#534AB7', marginBottom: 6 }}>{baslik}</div>
      <div style={{ fontSize: 13, color: textPrimary, lineHeight: 1.7 }}>{children}</div>
    </div>
  );
}

const inputSt: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box',
};
const selectSt: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13,
};
const btnSt: React.CSSProperties = {
  padding: '8px 18px', borderRadius: 6, border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontSize: 13,
};
const ulSt: React.CSSProperties = {
  margin: '6px 0 0', paddingLeft: 18,
};
